// Client REAL de `retail-wizard-business` (pista Retail 🔵 · dueño = Andrés). Una función por
// operationId del catálogo de `pantallas-wizard.html`. Todo va a SU base path del silo (regla
// puente D25) — jamás una URL de Fiado. Espejo del patrón de `retail-catalog.client.ts`.
//
// TD-F2-WIZ-002: el lambda 04 aún NO está en dev (commits locales, sesión 1). Hasta entonces la
// facade (index.ts) sirve el adapter mock. Cuando el silo despliegue: `VITE_WIZARD_MOCK=false`.
import type {
    CatalogItem,
    Beneficiary,
    ContractDocumentLink,
    ContractPreview,
    CreateProspectRequest,
    CreateSessionRequest,
    CreateSessionResponse,
    DownPaymentResult,
    EmailKind,
    FinancierOption,
    ScoreDecision,
    KycModality,
    KycPrepareResponse,
    KycState,
    PlanLevel,
    ProofOfAddressPrepareResponse,
    PromotionEffect,
    Prospect,
    RegisterDeviceResult,
    RegisterPaymentRequest,
    ScopeWidePromotion,
    SignatureSendOtpResult,
    SignatureSignResult,
    SkuPromotion,
} from '@/domains/sales/wizard/types';
import { apiFetch } from '../../client';
import type { ProspectResumeResponse } from '@fiado/type-kit/bin/retailWizard/dtos/responses/ProspectResumeResponse';
import type { components } from './retail-wizard.gen';
import { tagResumeOtpError } from './resumeOtpGuard';
import { toExternalProvider } from './externalProviderMapper';
import { toProspectByPhone, type ProspectByPhoneWire } from './prospectMapper';
import { toBackofficeSale, type BackofficeSaleWire } from './backofficeSaleMapper';
import { mxDayEndUtc, mxDayStartUtc } from '@/lib/dateHelpers';
import type {
    ChainedPages,
    BackofficeSale,
    BackofficeSalesFilters,
    BackofficeSalesPage,
    BackofficeSalesStats,
    CentralResendKind,
    CentralResendResult,
    PageQuery,
    PortalCatalogPage,
    PortalCatalogRow,
    PortalCustomerDetail,
    PortalCustomersPage,
    PortalCustomersQuery,
    PortalLevelFit,
    PortalSalePromotion,
    PortalSourceStatus,
    PortalLevelPlan,
    SaleDevice,
    SaleLogEntry,
    SalesPage,
    SaleSummary,
    SellerSalesListQuery,
    SellerSalesPage,
    SellerHomeTotals,
    StoreSummary,
    WelcomeBonus,
    WizardApi,
    WizardSessionCancel,
    CancelOtherStoreSaleResult,
    CustomerReferral,
} from './contract';
import {
    toAccountStatus,
    toActiveSession,
    toAppOnboarding,
    toDeviceEnrollment,
    toKycStatusResponse,
    toMdmProvider,
    toProductCategory,
    toProspectResume,
    toTrackingMode,
    toWizardSession,
    type AccountStatusWire,
    type ActiveSessionWire,
    type AppOnboardingWire,
    type DeviceEnrollmentWire,
    type KycStatusWire,
    type WizardSessionWire,
} from './sessionMapper';

const BASE_URL = import.meta.env.VITE_API_RETAIL_WIZARD_BASE_URL;

// Toda llamada al silo pasa por acá (`post` incluido), así que es el único lugar donde hay que
// reconocer el candado del OTP de la reanudación — el rebote no trae la sesión y el path sí.
function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    return apiFetch<T>(path, { ...init, baseUrl: BASE_URL }).catch((error: unknown) => {
        throw tagResumeOtpError(path, error);
    });
}

function post<T>(path: string, body?: unknown, idempotencyKey?: string): Promise<T> {
    return call<T>(path, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    });
}

const s = (id: string) => `/wizard/sessions/${id}`;

/** Tamaño de página que se le pide al silo: menos vueltas para la misma lista. */
const PAGE_LIMIT = 100;

/**
 * Techo de páginas encadenadas. Existe para que un listado enorme no dispare requests sin fin; al
 * topar se devuelve lo traído en vez de seguir.
 */
const MAX_CHAINED_PAGES = 20;

/** Cursor del listado como query string, listo para pegar detrás de `?index=seller`. */
function pageQuery(page: PageQuery | undefined): string {
    const parts: string[] = [];
    if (page?.limit !== undefined) parts.push(`limit=${String(page.limit)}`);
    if (page?.nextToken) parts.push(`nextToken=${encodeURIComponent(page.nextToken)}`);
    return parts.length > 0 ? `&${parts.join('&')}` : '';
}

/** Query de «Mis ventas»: el índice del vendedor más lo que venga puesto, nada vacío. */
function sellerSalesQuery(query: SellerSalesListQuery): string {
    const params = new URLSearchParams({ index: 'seller', limit: String(query.limit) });
    const { limit: _limit, counters, ...rest } = query;
    for (const [key, value] of Object.entries(rest)) {
        if (typeof value === 'string' && value !== '') params.set(key, value);
    }
    if (counters) params.set('counters', 'true');
    return params.toString();
}

/** Query de la cartera del portal: solo lo que venga puesto. */
function portalCustomersQuery(query: PortalCustomersQuery): string {
    const params = new URLSearchParams();
    if (query.limit !== undefined) params.set('limit', String(query.limit));
    if (query.nextToken) params.set('nextToken', query.nextToken);
    const q = query.q?.trim();
    if (q) params.set('q', q);
    const encoded = params.toString();
    return encoded === '' ? '' : `?${encoded}`;
}

/**
 * Junta todas las páginas de un listado del silo, y dice si se quedó corto.
 *
 * 🔴 El fin de la lista lo marca la AUSENCIA de `nextToken`, nunca una página vacía: el silo filtra
 * después del límite, así que una página puede venir sin ítems y tener más páginas detrás.
 *
 * 🔴 `truncated` no es adorno: al topar el techo se devolvía lo traído sin ninguna señal, así que
 * una lista incompleta se veía completa.
 */
async function collectPages<T>(
    fetchPage: (nextToken: string | undefined) => Promise<{ items: T[]; nextToken: string | null }>,
    onPage?: (collected: readonly T[]) => void,
): Promise<ChainedPages<T>> {
    const items: T[] = [];
    let nextToken: string | undefined;
    for (let page = 0; page < MAX_CHAINED_PAGES; page++) {
        const result = await fetchPage(nextToken);
        items.push(...result.items);
        if (!result.nextToken) return { items, truncated: false };
        onPage?.([...items]);
        nextToken = result.nextToken;
    }
    return { items, truncated: true };
}

/**
 * Versiones de los documentos legales que se registran al aceptar (D10). El silo las exige como
 * strings dentro de `consents` — mandar `{}` devuelve 400 VALIDATION_ERROR y deja la venta trabada
 * en el paso 01 (además de no resolver la identidad Fiado, que es lo que habilita el KYC).
 *
 * Hoy son placeholders: `GET /wizard/legal-documents/{type}` todavía es un esqueleto que responde
 * `version: null` (TD-005 del silo). En cuanto publique la versión real, se leen de ahí.
 */
const CONSENT_VERSIONS = { vlTermsVersion: 'v1', pcfContractVersion: 'v1' };

/**
 * Form del paso 10 → `WizardBeneficiary` del expediente (type-kit): nombre en una línea y el
 * parentesco al enum `BeneficiaryRelationEnum`; el texto libre viaja en `freeRelation` (OTHER).
 * Porcentaje fijo 100 — regla F2: beneficiario único, no editable.
 */
const RELATION_WIRE: Record<string, string> = {
    Cónyuge: 'SPOUSE',
    'Hijo(a)': 'CHILD',
    'Padre / Madre': 'PARENT',
    'Hermano(a)': 'SIBLING',
    'Abuelo(a)': 'GRANDPARENT',
    'Nieto(a)': 'GRANDCHILD',
    'Concubino(a)': 'PARTNER',
    'Amigo(a)': 'FRIEND',
    Otro: 'OTHER',
};
function toWireBeneficiary(b: Beneficiary): Record<string, unknown> {
    const relation = RELATION_WIRE[b.relationship] ?? 'OTHER';
    return {
        // fullName sigue viajando por retrocompatibilidad, pero los campos SEPARADOS son la fuente
        // correcta (type-kit >= 3.241.0): el esquema del expediente exige paternalLastName — el
        // aplanado perdía los apellidos justo donde el destino los requiere (400 reproducido).
        fullName: [b.firstName, b.lastNamePaternal, b.lastNameMaternal].filter(Boolean).join(' '),
        firstName: b.firstName,
        ...(b.lastNamePaternal ? { paternalLastName: b.lastNamePaternal } : {}),
        ...(b.lastNameMaternal ? { maternalLastName: b.lastNameMaternal } : {}),
        // El date picker ya produce ISO YYYY-MM-DD; vacío se omite.
        ...(b.birthDate ? { dateOfBirth: b.birthDate } : {}),
        // El caller resuelve el teléfono (titular cuando "mismo teléfono" está marcado).
        ...(b.phone ? { phoneNumber: b.phone } : {}),
        relation,
        // El reparto puede dividirse entre varios y el silo exige que la suma cierre en 100. Cuando
        // va uno solo el front no pide el número: se asume el total.
        percentage: b.percentage ?? 100,
        ...(relation === 'OTHER' ? { freeRelation: b.relationship } : {}),
    };
}

/** Wire del score (ScoreConsultResponse/ScoreResultResponse del type-kit — mismo shape). */
interface ScoreWire {
    status: string;
    level: string;
    sciEvaluationId: string;
    offer: {
        creditLine: number | null;
        downPayment: number | null;
        termsWeeks: number[];
        rates: Record<string, number>;
    } | null;
}

const SCORE_LEVELS = new Set(['NOT_ELIGIBLE', 'BRONZE', 'SILVER', 'GOLD']);

/** Decisión del motor → modelo del front (enums validados; offer null → undefined). */
function toScoreDecision(w: ScoreWire): ScoreDecision {
    return {
        status: w.status === 'APPROVED' || w.status === 'REJECTED' ? w.status : 'NO_ELIGIBLE',
        level: SCORE_LEVELS.has(w.level) ? (w.level as ScoreDecision['level']) : 'NOT_ELIGIBLE',
        sciEvaluationId: w.sciEvaluationId,
        offer: w.offer
            ? {
                  creditLine: w.offer.creditLine ?? undefined,
                  downPayment: w.offer.downPayment ?? undefined,
                  termsWeeks: w.offer.termsWeeks,
                  rates: w.offer.rates,
              }
            : undefined,
    };
}

/** Wire de `POST /quote` (QuoteResponse del type-kit: plan bounds + resumen del crédito en cents). */
interface QuoteWire {
    plan: {
        planId: string;
        code: string;
        minDownPaymentPct: number;
        maxDownPaymentPct: number | null;
        termWeeksOptions: number[];
        tnaAnnual: number;
        openingCommissionPct: number;
    };
    credit: {
        creditId: string;
        status: string;
        planId: string;
        termWeeks: number;
        equipmentPriceCents: number;
        downPaymentCents: number;
        financedCapitalCents: number;
        tnaAnnual: number;
        weeklyPaymentCents: number;
        weeklyPaymentWithIvaCents: number;
        totalInterestCents: number;
        totalIvaCents: number;
        totalToPayCents: number;
    };
    /** Cotas reales con las que se cotizó, ya pisadas por la promoción y las políticas. */
    effectiveTerms?: {
        minDownPaymentPct: number;
        maxDownPaymentPct: number | null;
        maxFinancedAmountCents?: number | null;
    } | null;
    /** Bono de la promo de crédito ganadora; ausente si no hay. */
    creditPromotionBonusCents?: number | null;
}

/** Wire de `GET /financiers` (SessionFinancierOption del type-kit `retailWizard`). */
interface SessionFinancierWire {
    code: string;
    name: string;
    label: string;
    type: string;
    displayOrder: number;
    externalRedirectUrl: string | null;
    eligible: boolean;
    ineligibleReason?: FinancierOption['ineligibleReason'] | null;
}

type WizardCatalogItemGen = components['schemas']['WizardCatalogItem'];

/** Condición de una pieza devuelta al cancelar; lo no listado vuelve en buen estado. */
export type ReturnPieceCondition = components['schemas']['ReturnPieceCondition'];

/** Fila del catálogo del silo: una por SKU (producto suelto, kit o servicio), con su disponible. */
interface CatalogItemWire {
    sku: string;
    /** Ausente en un silo anterior a los servicios. */
    itemType?: WizardCatalogItemGen['itemType'];
    stock: WizardCatalogItemGen['stock'];
    available: boolean;
    /** Precio vigente de la línea (el del kit si es kit). */
    basePriceCents: number;
    cashPriceCents: number;
    category: string;
    trackingMode: string;
    /** Solo si es kit. */
    components?: { sku: string; qty: number }[] | null;
    /** Ausentes en kits y servicios dados de alta sin marca ni modelo. */
    brand?: string | null;
    model?: string | null;
    /** Nombre visible; lo traen kits, servicios y productos que lo tengan. */
    description?: string | null;
    capacity?: string | null;
    /** RAM en GB como texto; provisional hasta el próximo `sync-api-types`. */
    ram?: string | null;
    color?: string | null;
    imageUrl: string | null;
    /** `null` = sin gama. */
    tier: string | null;
    eligibleFinanciers: string[];
    financeable: boolean;
    mdmEnabled: boolean;
    mdmProvider: string | null;
    mdmLockMode: string | null;
    hasPromotion: boolean;
    affordableOnCredit?: boolean | null;
    minDownPaymentCents?: number | null;
    /** Enganche mínimo del plan del nivel y tope del nivel, repetidos por fila. */
    minDownPaymentPct?: number | null;
    creditCapCents?: number | null;
    /** Prioridad de venta 0–100; opcional hasta que el wizard la exponga. */
    salePriority?: number | null;
    promotions?: CatalogPromotionWire[] | null;
}

/**
 * Promo de la fila. Sin `kind` o con `CREDIT` es de crédito (con `effect`); `SALE` es de venta.
 * Campos de venta provisionales hasta el próximo `sync-api-types`.
 */
interface CatalogPromotionWire {
    kind?: 'CREDIT' | 'SALE' | null;
    promotionId?: string | null;
    name?: string | null;
    effect?: Record<string, number | null | undefined> | null;
    targetLevels?: string[] | null;
    benefitSummary?: string | null;
    validFrom?: string | null;
    validUntil?: string | null;
}

/** Una promoción scope-wide del wire — misma laxitud que `CatalogItemWire['promotions']`. */
interface ScopeWidePromotionWire {
    promotionId?: string | null;
    name?: string | null;
    description?: string | null;
    effect?: Record<string, number | null | undefined> | null;
    segmentId?: string | null;
    targetLevels?: string[] | null;
}

/** `GET /wizard/catalog`: filas por SKU y las promociones de pantalla completa. */
interface CatalogWireResponse {
    items: CatalogItemWire[];
    scopeWidePromotions?: ScopeWidePromotionWire[] | null;
    /** Tope del nivel; se omite sin sesión, sin nivel, NOT_ELIGIBLE o sin plan. */
    creditCapCents?: number | null;
    /** Enganche mínimo del plan del nivel (decimal sobre 1); el silo aún no lo manda. */
    minDownPaymentPct?: number | null;
}

type PortalLevelPlanGen = components['schemas']['PortalLevelPlan'];

/** Fila de `GET /portal/catalog`: la del catálogo sin cotas de sesión, más sus niveles. */
interface PortalCatalogItemWire extends CatalogItemWire {
    levels: components['schemas']['PortalLevelFit'][] | null;
}

/** `GET /portal/catalog`: el envelope de catálogo viaja solo en la primera página. */
interface PortalCatalogWireResponse {
    items: PortalCatalogItemWire[];
    nextToken?: string;
    scopeWidePromotions?: ScopeWidePromotionWire[] | null;
    levelPlans?: PortalLevelPlanGen[] | null;
    sources?: { salePromotions?: PortalSourceStatus | null } | null;
}

/** Gama → prioridad de orden. Heurística de PRESENTACIÓN: el silo no define un ranking comercial. */
const TIER_RANK: Record<string, number> = { PREMIUM: 3, HIGH: 2, MID: 1 };

/** Niveles que el front sabe pintar. Uno que no conozca se descarta: no se inventa una etiqueta. */
const PLAN_LEVELS = new Set<PlanLevel>(['BRONZE', 'SILVER', 'GOLD']);

/** Un número real y distinto de cero; lo demás no es un efecto que valga la pena anunciar. */
function usableAmount(value: number | null | undefined): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) && value !== 0 ? value : undefined;
}

/** Arma el `PromotionEffect` a partir de los campos numéricos nullable del wire. */
function toPromotionEffect(
    wire: Record<string, number | null | undefined> | null | undefined,
): PromotionEffect {
    return {
        downPaymentDeltaPct: usableAmount(wire?.downPaymentDeltaPct),
        pcfBonusCents: usableAmount(wire?.pcfBonusCents),
        tnaOverride: usableAmount(wire?.tnaOverride),
        maxAmountOverrideCents: usableAmount(wire?.maxAmountOverrideCents),
    };
}

/** Filtra `targetLevels` del wire contra los niveles que el front sabe pintar. */
function toTargetLevels(wire: string[] | null | undefined): PlanLevel[] {
    return (wire ?? []).filter((l): l is PlanLevel => PLAN_LEVELS.has(l as PlanLevel));
}

/**
 * Promociones del wire a las del panel. Una promoción sin id, sin nombre o sin ningún efecto
 * legible se descarta: un renglón vacío en el panel es peor que un renglón de menos.
 */
function toSkuPromotions(wire: CatalogItemWire['promotions']): SkuPromotion[] {
    if (!Array.isArray(wire)) return [];
    return wire.flatMap((p) => {
        if (p.kind === 'SALE') return [];
        const promotionId = p.promotionId;
        const name = p.name?.trim();
        if (!promotionId || !name) return [];
        const effect = toPromotionEffect(p.effect);
        if (Object.values(effect).every((v) => v === undefined)) return [];
        const targetLevels = toTargetLevels(p.targetLevels);
        return [{ promotionId, name, effect, targetLevels }];
    });
}

/**
 * Promociones scope-wide del wire al banner. Mismo criterio que `toSkuPromotions`: sin id, sin
 * nombre o sin ningún efecto legible se descarta.
 */
function toScopeWidePromotions(
    wire: ScopeWidePromotionWire[] | null | undefined,
): ScopeWidePromotion[] {
    if (!Array.isArray(wire)) return [];
    return wire.flatMap((p) => {
        const promotionId = p.promotionId;
        const name = p.name?.trim();
        if (!promotionId || !name) return [];
        const effect = toPromotionEffect(p.effect);
        if (Object.values(effect).every((v) => v === undefined)) return [];
        const targetLevels = toTargetLevels(p.targetLevels);
        return [
            {
                promotionId,
                name,
                description: p.description?.trim() ?? '',
                effect,
                segmentId: p.segmentId ?? '',
                targetLevels,
            },
        ];
    });
}

/**
 * Fila del silo a la tarjeta. `priorityRank` es lo único que el silo no expone: se deriva del `tier`
 * como heurística de presentación. Capacidad, color y gama vacíos se pintan como «sin dato».
 */
function toCatalogItem(w: CatalogItemWire): CatalogItem {
    return {
        sku: w.sku,
        brand: w.brand ?? '',
        model: w.model ?? '',
        ...(w.description ? { description: w.description } : {}),
        capacity: w.capacity ?? '',
        ram: w.ram ?? null,
        color: w.color ?? '',
        cashPriceCents: w.basePriceCents,
        itemType: w.itemType ?? 'PRODUCT',
        stock: w.stock,
        category: toProductCategory(w.category),
        trackingMode: toTrackingMode(w.trackingMode),
        components: (w.components ?? []).map((c) => ({ sku: c.sku, qty: c.qty })),
        financeable: w.financeable,
        eligibleFinanciers: w.eligibleFinanciers,
        tier: w.tier ?? '',
        priorityRank: TIER_RANK[w.tier ?? ''] ?? 0,
        ...(typeof w.salePriority === 'number' ? { salePriority: w.salePriority } : {}),
        hasPromo: w.hasPromotion,
        // `null` (sin nivel o sin plan) se normaliza a `undefined`: no es un «no alcanza».
        affordableOnCredit: w.affordableOnCredit ?? undefined,
        ...(typeof w.minDownPaymentCents === 'number'
            ? { minDownPaymentCents: w.minDownPaymentCents }
            : {}),
        ...(typeof w.minDownPaymentPct === 'number'
            ? { minDownPaymentPct: w.minDownPaymentPct }
            : {}),
        ...(typeof w.creditCapCents === 'number' ? { creditCapCents: w.creditCapCents } : {}),
        promotions: toSkuPromotions(w.promotions),
        imageUrl: w.imageUrl,
        mdmEnabled: w.mdmEnabled,
        mdmProvider: w.mdmProvider,
        mdmLockMode: w.mdmLockMode,
    };
}

/** Niveles del wire que el front sabe pintar; `null` (planes sin leer) se queda en `null`. */
function toPortalLevelFits(wire: PortalCatalogItemWire['levels']): PortalLevelFit[] | null {
    if (!Array.isArray(wire)) return null;
    return wire.flatMap((fit) =>
        PLAN_LEVELS.has(fit.level)
            ? [
                  {
                      level: fit.level,
                      eligible: fit.eligible,
                      ...(typeof fit.minDownPaymentCents === 'number'
                          ? { minDownPaymentCents: fit.minDownPaymentCents }
                          : {}),
                  },
              ]
            : [],
    );
}

function toPortalLevelPlans(wire: PortalLevelPlanGen[] | null): PortalLevelPlan[] | null {
    if (!Array.isArray(wire)) return null;
    return wire.filter((plan): plan is PortalLevelPlan => PLAN_LEVELS.has(plan.level));
}

/** Las promos de venta de la fila; sin id o sin nombre se descartan. */
function toSalePromotions(wire: CatalogItemWire['promotions']): PortalSalePromotion[] {
    if (!Array.isArray(wire)) return [];
    return wire.flatMap((p) => {
        const name = p.name?.trim();
        if (p.kind !== 'SALE' || !p.promotionId || !name) return [];
        return [
            {
                promotionId: p.promotionId,
                name,
                benefitSummary: p.benefitSummary?.trim() ?? '',
                validFrom: p.validFrom ?? null,
                validUntil: p.validUntil ?? null,
            },
        ];
    });
}

function toPortalCatalogPage(wire: PortalCatalogWireResponse): PortalCatalogPage {
    const saleDown = wire.sources?.salePromotions === 'UNAVAILABLE';
    return {
        items: wire.items.map((item) => ({
            ...toCatalogItem(item),
            levels: toPortalLevelFits(item.levels),
            salePromotions: saleDown ? null : toSalePromotions(item.promotions),
        })),
        ...(wire.nextToken ? { nextToken: wire.nextToken } : {}),
        ...(wire.scopeWidePromotions !== undefined
            ? { scopeWidePromotions: toScopeWidePromotions(wire.scopeWidePromotions) }
            : {}),
        ...(wire.levelPlans !== undefined
            ? { levelPlans: toPortalLevelPlans(wire.levelPlans) }
            : {}),
    };
}

export const wizardClient: WizardApi = {
    createSession: (req: CreateSessionRequest) =>
        post<CreateSessionResponse>('/wizard/sessions', req, crypto.randomUUID()),
    // El lambda devuelve `{ session, otps }` (SessionWithOtps), no la sesión pelada. Sin desenvolver,
    // todas las pantallas reciben un objeto sin `phone`/`currentStep` y revientan
    // ("Cannot read properties of undefined (reading 'replace')" en formatPhone del paso 01).
    // Y lo que viene adentro es la fila PLANA de la saga, no el modelo de vista: `toWizardSession`
    // la traduce (ver sessionMapper.ts). Sin ese paso la pantalla 02 revienta en `session.kyc.status`.
    getSession: async (id) =>
        toWizardSession((await call<{ session: WizardSessionWire }>(s(id))).session),
    // El teléfono viaja como filtro de la colección, no como sub-ruta: `/wizard/sessions/active`
    // sería hermana constante de `/wizard/sessions/{sessionId}` y API Gateway rechaza la ambigüedad.
    getActiveSessionByPhone: async (phone) =>
        toActiveSession(
            await call<ActiveSessionWire>(`/wizard/sessions?phone=${encodeURIComponent(phone)}`),
        ),
    // Devuelve la sesión cancelada; de ella se leen el reembolso y las líneas aún por liberar.
    cancelSession: async (id, reason) => {
        const row = await post<
            (WizardSessionCancel & Pick<WizardSessionWire, 'reservedLines'>) | null
        >(`${s(id)}/cancel`, { reason }, crypto.randomUUID());
        const releasingLines = (row?.reservedLines ?? []).filter(
            (line) => line.status === 'RELEASING',
        ).length;
        return {
            ...(row?.refundPendingCents !== undefined && {
                refundPendingCents: row.refundPendingCents,
            }),
            ...(releasingLines > 0 && { releasingLines }),
        };
    },
    cancelPhoneHolder: (req) =>
        post<CancelOtherStoreSaleResult>(
            '/wizard/phone-holder/cancel',
            { phone: req.phone, reason: req.reason.trim() },
            crypto.randomUUID(),
        ),
    // Devuelve la fila PLANA de la saga, igual que el GET — con el OTP ya marcado como pendiente y
    // el `resumeTarget` del paso al que hay que aterrizar.
    resumeSession: async (id) =>
        toWizardSession(
            await post<WizardSessionWire>(`${s(id)}/resume`, undefined, crypto.randomUUID()),
        ),
    verifyOtp: (id, code) => post<null>(`${s(id)}/otp/verify`, { code }),
    resendOtp: (id) => post<{ resendAvailableAt: string }>(`${s(id)}/otp/resend`),
    acceptConsents: (id) =>
        post<null>(
            `${s(id)}/consents`,
            { consents: { ...CONSENT_VERSIONS, acceptedAt: new Date().toISOString() } },
            crypto.randomUUID(),
        ),
    sendEmailOtp: async (id, email, kind: EmailKind) => {
        const body: components['schemas']['SendEmailOtpRequest'] = {
            email: email.trim(),
            type: kind,
        };
        return toWizardSession(await post<WizardSessionWire>(`${s(id)}/email/otp/send`, body));
    },
    verifyEmailOtp: async (id, code) => {
        const body: components['schemas']['VerifyEmailOtpRequest'] = { code };
        return toWizardSession(await post<WizardSessionWire>(`${s(id)}/email/otp/verify`, body));
    },
    getLegalDocument: (type) => call<{ url: string }>(`/wizard/legal-documents/${type}`),

    kycPrepare: (id, modality: KycModality) =>
        post<KycPrepareResponse>(`${s(id)}/kyc/prepare`, { modality }),
    // Se mapea, NO se castea: el cable devuelve `{kycStatus, accountStatus, subChecks}` y `KycState`
    // es el tipo de dominio. Un `call<KycState>` dejaba `tsc` verde prometiendo campos que el silo
    // nunca manda — `undefined` en Dev, correcto en el mock.
    kycStatus: async (id) =>
        toKycStatusResponse(await call<KycStatusWire>(`${s(id)}/kyc/status`)) as KycState,
    // El comprobante de domicilio es un flow SEPARADO del documento de identidad: su propio endpoint,
    // su propio flowId. El resultado no vuelve por aquí, llega por el poll de `kyc/status`.
    proofOfAddressPrepare: (id, modality: KycModality) =>
        post<ProofOfAddressPrepareResponse>(`${s(id)}/proof-of-address/prepare`, { modality }),
    kycSendLink: (id) => post<null>(`${s(id)}/kyc/send-link`),
    kycManual: (id, data) => post<null>(`${s(id)}/kyc/manual`, data, crypto.randomUUID()),
    // El silo nombra los campos `occupation`/`occupationOther` (OccupationRequest). Con `code`/
    // `otherText` respondía 400 VALIDATION_ERROR y el expediente Nivel 1 nunca se completaba.
    // `monthlyIncome` sí se llama igual de los dos lados.
    // Los MXN_* solo viajan si vienen: la venta los omite.
    setOccupation: (id, data) => {
        const body: components['schemas']['OccupationRequest'] = {
            occupation: data.code,
            occupationOther: data.otherText,
            monthlyIncome: data.monthlyIncome,
            ...(data.monthlyExtraSupport && { monthlyExtraSupport: data.monthlyExtraSupport }),
            ...(data.usageMonthly && { usageMonthly: data.usageMonthly }),
            ...(data.maximumTx && { maximumTx: data.maximumTx }),
            ...(data.beneficialOwner && { beneficialOwner: data.beneficialOwner }),
        };
        return post<null>(`${s(id)}/occupation`, body);
    },
    // Se mapea, NO se castea: los dos enums del cable se validan antes de entrar al dominio. Un valor
    // desconocido casteado a `FAILED` bloquearía una venta sana. Ver `toAccountStatus`.
    accountStatus: async (id) =>
        toAccountStatus(await call<AccountStatusWire>(`${s(id)}/account-status`)),

    // `?session=` es lo ÚNICO que se manda. El endpoint resuelve la tienda por la identidad del
    // vendedor (token → retail-org) y devuelve el inventario completo; `sort`/`q` viajaban antes y
    // el controller los descartaba en silencio. Se filtra en el cliente (`catalogFilters.ts`).
    getCatalog: async (id) => {
        const wire = await call<CatalogWireResponse>(
            `/wizard/catalog?${new URLSearchParams({ session: id }).toString()}`,
        );
        // `syncedAt` = ahora: la respuesta viene del silo en vivo, no de una copia local.
        return {
            items: wire.items.map(toCatalogItem),
            syncedAt: new Date().toISOString(),
            scopeWidePromotions: toScopeWidePromotions(wire.scopeWidePromotions),
            ...(typeof wire.creditCapCents === 'number'
                ? { creditCapCents: wire.creditCapCents }
                : {}),
            ...(typeof wire.minDownPaymentPct === 'number'
                ? { minDownPaymentPct: wire.minDownPaymentPct }
                : {}),
        };
    },
    // Responden la fila de la saga con `reservedLines`: se traduce igual que el GET de la sesión.
    selectProduct: async (id, req, idempotencyKey) =>
        toWizardSession(
            await post<WizardSessionWire>(
                `${s(id)}/product`,
                req,
                idempotencyKey ?? crypto.randomUUID(),
            ),
        ),
    renewLineReservation: async (id, lineId) =>
        toWizardSession(
            await post<WizardSessionWire>(
                `${s(id)}/lines/${encodeURIComponent(lineId)}/reservation/renew`,
                undefined,
                crypto.randomUUID(),
            ),
        ),
    releaseProduct: async (id, target) =>
        toWizardSession(
            await call<WizardSessionWire>(`${s(id)}/product`, {
                method: 'DELETE',
                body: JSON.stringify(target),
            }),
        ),
    suggestIdentifiers: async (id, lineId, sku, prefix) => {
        const query = new URLSearchParams({ sku, prefix }).toString();
        const pieces = await call<{ identifier: string }[]>(
            `${s(id)}/lines/${encodeURIComponent(lineId)}/identifiers?${query}`,
        );
        return pieces.map((p) => p.identifier);
    },

    // GET /financiers — opciones del paso 04 (catálogo SOFOM × elegibilidad del producto). El wire
    // es `SessionFinanciersResponse` del type-kit; se ordena por displayOrder y se normaliza el
    // null de externalRedirectUrl.
    listFinanciers: async (id): Promise<FinancierOption[]> => {
        const r = await call<{ financiers: SessionFinancierWire[] }>(`${s(id)}/financiers`);
        return [...r.financiers]
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((f) => ({
                code: f.code,
                name: f.name,
                label: f.label,
                type: f.type === 'INTERNAL' ? 'INTERNAL' : 'EXTERNAL',
                displayOrder: f.displayOrder,
                externalRedirectUrl: f.externalRedirectUrl ?? undefined,
                eligible: f.eligible,
                ...(f.ineligibleReason ? { ineligibleReason: f.ineligibleReason } : {}),
            }));
    },
    // Paso 05 — el consult devuelve la decisión DIRECTA (idempotente por sesión: si ya hay
    // snapshot, lo devuelve sin re-evaluar); el result relee el snapshot al reanudar.
    scoreConsult: async (id) =>
        toScoreDecision(await post<ScoreWire>(`${s(id)}/score/consult`, {}, crypto.randomUUID())),
    scoreResult: async (id) => toScoreDecision(await call<ScoreWire>(`${s(id)}/score/result`)),

    // Paso 06 — 1ª llamada ORIGINA el crédito (idempotente por sesión en el motor); las siguientes
    // RE-COTIZAN el crédito PENDING. Wire = QuoteResponse del type-kit (plan bounds + summary en cents).
    quote: async (id, terms) => {
        const r = await post<QuoteWire>(`${s(id)}/quote`, terms);
        return {
            plan: {
                planId: r.plan.planId,
                code: r.plan.code,
                minDownPaymentPct: r.plan.minDownPaymentPct,
                maxDownPaymentPct: r.plan.maxDownPaymentPct ?? undefined,
                termWeeksOptions: r.plan.termWeeksOptions,
                tnaAnnual: r.plan.tnaAnnual,
                openingCommissionPct: r.plan.openingCommissionPct,
            },
            credit: r.credit,
            effectiveTerms: r.effectiveTerms ?? undefined,
            ...(typeof r.creditPromotionBonusCents === 'number' &&
                r.creditPromotionBonusCents > 0 && {
                    creditPromotionBonusCents: r.creditPromotionBonusCents,
                }),
        };
    },

    // El front usa `CASH_OR_CARD` internamente; el backend espera el enum `PaymentMethodEnum`
    // (`CASH_SALE`). Se traduce AQUÍ, en el borde, simétrico al `sessionMapper` que hace CASH_SALE →
    // CASH_OR_CARD en la lectura. El campo del DTO es `paymentMethod`, no `method`.
    setPaymentMethod: (id, method) =>
        post<null>(`${s(id)}/payment-method`, {
            paymentMethod: method === 'CASH_OR_CARD' ? 'CASH_SALE' : method,
        }),
    // El DTO es `ExternalReferralRequest { provider }`; el código del catálogo se traduce al enum.
    externalReferral: async (id, financierCode) => {
        const wire = await post<WizardSessionWire | null>(`${s(id)}/external-referral`, {
            provider: toExternalProvider(financierCode),
        });
        return wire ? toWizardSession(wire) : null;
    },

    getFolio: (id) => post<{ folio: string }>(`${s(id)}/payment/folio`, {}, crypto.randomUUID()),
    // `payments` ya viaja con el enum del wire (`WIRE`), armado en `toSalePayments`.
    registerPayment: (id, req: RegisterPaymentRequest) =>
        post<null>(`${s(id)}/payment`, req, crypto.randomUUID()),

    // Paso 07 crédito — el folio ENG lo genera el server DENTRO de este endpoint (no llamar
    // `/payment/folio` en la pista crédito: ese es del contado y produce otro formato de folio).
    downPayment: (id, req: RegisterPaymentRequest) =>
        post<DownPaymentResult>(`${s(id)}/down-payment`, req, crypto.randomUUID()),
    // Paso 08 (y rehidratación del 07) — el shape del wire (ContractPreviewResponse) coincide 1:1.
    contractPreview: (id) => call<ContractPreview>(`${s(id)}/contract/preview`),
    contractDocument: (id) => call<ContractDocumentLink>(`${s(id)}/contract/document`),
    // Tiempo 1: el cliente aceptó los términos; sin esa aceptación el servidor no manda el código.
    // SIN idempotency-key a propósito: re-llamar REENVÍA el OTP de firma (es la semántica).
    signatureSendOtp: (id) =>
        post<SignatureSendOtpResult>(`${s(id)}/signature/send-otp`, { termsAccepted: true }),
    signatureSign: (id, otpCode) =>
        post<SignatureSignResult>(`${s(id)}/signature/sign`, { otpCode }),
    // Paso 09 crédito — tiempo 1: registra el IMEI y abre el enrolamiento. La key la pone el hook,
    // una por intento de (sesión, IMEI). El proveedor se narra igual que en el GET.
    registerDevice: async (id, imei, idempotencyKey) => {
        const result = await post<RegisterDeviceResult>(
            `${s(id)}/device`,
            { imei },
            idempotencyKey,
        );
        return { ...result, mdmProvider: toMdmProvider(result.mdmProvider) };
    },
    // Tiempo 2 — se pollea mientras el vendedor instala el candado en el equipo. Por el mapper y no
    // por un cast: un status desconocido tiene que caer en "no pudimos confirmar", no en "espera".
    getDeviceEnrollment: async (id) =>
        toDeviceEnrollment(await call<DeviceEnrollmentWire>(`${s(id)}/device/enrollment`)),

    // Responde la fila de la saga: se traduce igual que el GET de la sesión.
    pinPieces: async (id, lines) => {
        const body: components['schemas']['PinSessionPiecesRequest'] = { lines };
        return toWizardSession(
            await post<WizardSessionWire>(`${s(id)}/pieces`, body, crypto.randomUUID()),
        );
    },
    completeSale: (id, lines) =>
        post<{ saleId: string }>(
            `${s(id)}/complete-sale`,
            lines ? { lines } : {},
            crypto.randomUUID(),
        ),
    completeAccountOpening: async (id) =>
        toWizardSession(
            await post<WizardSessionWire>(
                `${s(id)}/account-opening/complete`,
                undefined,
                crypto.randomUUID(),
            ),
        ),
    getAppInstallLink: (id) =>
        post<{ shortUrl: string; directoryId: string }>(`${s(id)}/app/install-link`),
    // Por el mapper y no por un cast: un status que este front no conoce tiene que caer del lado de
    // "no pudimos verificarlo", nunca del de "el cliente no lo ha hecho". Ver `toAppOnboarding`.
    getAppOnboarding: async (id) =>
        toAppOnboarding(await call<AppOnboardingWire>(`${s(id)}/app-onboarding`)),
    markCardDelivered: (id) =>
        post<null>(`${s(id)}/pcf-card/mark-delivered`, {}, crypto.randomUUID()),

    getWelcomeBonus: (id) => call<WelcomeBonus>(`${s(id)}/welcome-bonus`),
    // Wire real (ConfirmAddressRequest): `confirmed` obligatorio + `address?: AddressBase` opcional.
    getProfile: (id) => call<ProfileState>(`${s(id)}/profile`),
    searchAddress: (id, q) =>
        call<AddressCandidate[]>(`${s(id)}/address-search?q=${encodeURIComponent(q)}`),
    setProfileQuestions: (id, answers: ProfileAnswers) =>
        post<null>(`${s(id)}/profile-questions`, answers, crypto.randomUUID()),
    // El silo YA persiste (IAddressApi, TD-003 cerrado). El domicilio NUEVO viaja estructurado tal
    // como lo devolvió el buscador — calle, colonia, CP y coordenadas. Antes se mandaba la línea
    // libre en `street`, y así el domicilio entra sin CP confiable ni geometría: son las filas con
    // `point: [0,0]` que el backoffice marca "sin coordenadas", y ese dato lo consume Pomelo.
    //
    // Sin `address` solo ratifica: el lambda apila y volver a escribirlo duplicaría la fila.
    confirmAddress: (id, useIneAddress, address) =>
        post<null>(
            `${s(id)}/address/confirm`,
            address ? { confirmed: useIneAddress, address } : { confirmed: useIneAddress },
            crypto.randomUUID(),
        ),
    // Wire real (SaveBeneficiaryRequest): `{ beneficiaries: WizardBeneficiary[] }` — shape del
    // expediente (fullName + relation enum + percentage), NO el form del front. Se traduce acá.
    //
    // El reparto COMPLETO viaja junto: el destino ACUMULA, así que mandarlos de a uno dejaría el
    // expediente repartido a medias si falla el segundo. El silo valida que la suma cierre en 100.
    setBeneficiary: (id, beneficiaries: Beneficiary[]) =>
        post<null>(
            `${s(id)}/beneficiary`,
            { beneficiaries: beneficiaries.map(toWireBeneficiary) },
            crypto.randomUUID(),
        ),
    // `annexVersion` es obligatorio; 'v1' es el placeholder mientras GET /legal-documents publica
    // la versión real (mismo TD-005 que CONSENT_VERSIONS del paso 01).
    signAnnex: (id) =>
        post<null>(`${s(id)}/beneficiary-annex/sign`, { annexVersion: 'v1' }, crypto.randomUUID()),
    welcomeTopup: (id) => post<null>(`${s(id)}/welcome-topup`, {}, crypto.randomUUID()),
    // "Salir sin recarga" NO es otro endpoint: es el mismo /welcome-topup con `skip: true`
    // (PAUSED_NO_TOPUP — la venta ya cerró; el bono queda disponible para otra visita).
    exitWithoutTopup: (id) =>
        post<null>(`${s(id)}/welcome-topup`, { skip: true }, crypto.randomUUID()),

    listSessionReferrals: async (id) => {
        const r = await call<{ referrals?: CustomerReferral[] } | null>(`${s(id)}/referrals`);
        return r?.referrals ?? [];
    },
    saveSessionReferrals: async (id, referrals) => {
        const body: components['schemas']['SaveSessionReferralsRequest'] = { referrals };
        const r = await post<{ referrals?: CustomerReferral[] } | null>(
            `${s(id)}/referrals`,
            body,
            crypto.randomUUID(),
        );
        return r?.referrals ?? [];
    },
    skipReferrals: (id) => post<null>(`${s(id)}/referrals/skip`, undefined, crypto.randomUUID()),
    updateSessionReferral: async (id, referralId, patch) => {
        const r = await call<{ referral?: CustomerReferral } | null>(
            `${s(id)}/referrals/${encodeURIComponent(referralId)}`,
            { method: 'PUT', body: JSON.stringify(patch) },
        );
        return r?.referral ?? null;
    },

    // Los prospectos NO cuelgan de `/wizard/*` en el contrato del silo: viven en la raíz
    // (`/prospects`, `/prospects/{id}/mark-lost`). Con el prefijo, API Gateway no matchea ninguna ruta
    // y responde el genérico `{"message":"Missing Authentication Token"}` — que parece un problema de
    // auth pero es un 404 de ruta.
    // El `sellerId` tampoco viaja: el lambda IGNORA cualquier identidad que mande el cliente y fuerza
    // el scope del token del vendedor (fix de IDOR del silo). Solo se elige el `index`.
    // Los listados del silo vienen paginados como `{ items, count, nextToken }`, no como array
    // pelado. La página viaja ENTERA hacia arriba: quedarse con `items` truncaba la lista en
    // silencio, y el índice del silo se recorre de la venta más VIEJA a la más nueva.
    listSales: (page) => call<SalesPage>(`/sales?index=seller${pageQuery(page)}`),
    // La tienda tampoco viaja: el silo la toma del token del vendedor.
    listStoreSales: (page) => call<SalesPage>(`/sales?index=store${pageQuery(page)}`),
    listSellerSales: (query) => call<SellerSalesPage>(`/sales?${sellerSalesQuery(query)}`),
    getSale: (saleId) => call<SaleSummary>(`/sales/${encodeURIComponent(saleId)}`),
    listPortalCatalog: async (page) => {
        const query = pageQuery(page).replace(/^&/, '');
        return toPortalCatalogPage(
            await call<PortalCatalogWireResponse>(
                query === '' ? '/portal/catalog' : `/portal/catalog?${query}`,
            ),
        );
    },
    listPortalCatalogAll: async (onProgress) => {
        let scopeWidePromotions: ScopeWidePromotion[] = [];
        const chained = await collectPages<PortalCatalogRow>(
            async (nextToken) => {
                const page = await wizardClient.listPortalCatalog({ limit: PAGE_LIMIT, nextToken });
                if (nextToken === undefined) scopeWidePromotions = page.scopeWidePromotions ?? [];
                return { items: page.items, nextToken: page.nextToken ?? null };
            },
            (items) => {
                onProgress?.({
                    items: [...items],
                    truncated: false,
                    scopeWidePromotions,
                    complete: false,
                });
            },
        );
        return { ...chained, scopeWidePromotions, complete: true };
    },
    listPortalCustomers: (query) =>
        call<PortalCustomersPage>(`/portal/customers${portalCustomersQuery(query)}`),
    getPortalCustomer: (retailCustomerId) =>
        call<PortalCustomerDetail>(`/portal/customers/${encodeURIComponent(retailCustomerId)}`),
    // El expediente del equipo NO viene con la venta: es una lectura aparte contra el motor de
    // créditos, y por eso solo se pide con una venta abierta.
    getSaleDevice: (saleId) => call<SaleDevice>(`/sales/${encodeURIComponent(saleId)}/device`),
    getSellerHome: () => call<SellerHomeTotals>('/seller/home'),
    // El rango viaja como query param; el silo acota la tienda al scope del token, igual que
    // `/seller/home` — mandar un storeId desde el front no cambiaría nada y sería un IDOR.
    getStoreSummary: (range) =>
        call<StoreSummary>(`/seller/store-summary?range=${encodeURIComponent(range)}`),

    // El drawer necesita los prospectos COMPLETOS, así que aquí se encadena el cursor: una sola
    // página dejaba fuera a los que no entraban, sin que la pantalla pudiera saberlo.
    listProspects: () =>
        collectPages<Prospect>((nextToken) =>
            call<{ items: Prospect[]; nextToken: string | null }>(
                `/prospects?index=seller${pageQuery({ limit: PAGE_LIMIT, nextToken })}`,
            ),
        ),
    createProspect: (req: CreateProspectRequest) =>
        post<Prospect>('/prospects', req, crypto.randomUUID()),
    // Devuelve solo lo que el drawer precarga, sin `prospectId`: la sobreescritura es por teléfono.
    // `found: false` llega en 200, y un teléfono mal formado en 400 `PHONE_FILTER_INVALID`.
    getProspectByPhone: async (phone) =>
        toProspectByPhone(
            await call<ProspectByPhoneWire>(
                `/prospects/by-phone?phone=${encodeURIComponent(phone)}`,
            ),
        ),
    // El motivo es OBLIGATORIO en el DTO del silo: sin body, `@IsString` rebota con 400.
    markProspectLost: (prospectId, reason) =>
        post<null>(`/prospects/${prospectId}/mark-lost`, { reason }),
    resumeProspect: async (prospectId) =>
        toProspectResume(
            await post<ProspectResumeResponse>(
                `/prospects/${prospectId}/resume`,
                undefined,
                crypto.randomUUID(),
            ),
        ),
};

// ── Backoffice · sección Ventas ───────────────────────────────────────────────
// Endpoints en scope TENANT, SEPARADOS de `wizardApi` a propósito: el listado del vendedor
// (`listSales`, arriba) está acotado a su tienda por un fix de IDOR del silo y no se amplía.

/**
 * Traduce los filtros de la UI a query params del endpoint de BO.
 *
 * Dos reglas que no son obvias:
 * 1. Tienda, vendedor y cliente NO son filtros: son ÍNDICES. El endpoint hace Query sobre el GSI
 *    correspondiente, así que elegir tienda cambia `index`+`value`, no agrega un parámetro.
 *    Precedencia: cliente > vendedor > tienda > cadena (del más específico al más amplio).
 * 2. Los montos se capturan en pesos y viajan en centavos: mandar pesos filtraría 100x más barato.
 */
export function buildBackofficeSalesQuery(f: BackofficeSalesFilters): string {
    const p = new URLSearchParams();

    // La partición SIEMPRE es la cadena. Tienda, vendedor y cliente son filtros TERRITORIALES que
    // acotan dentro de ella, no índices que la muevan.
    //
    // Antes se mandaban como `index=store&value=<id>`, que era el contrato F2. Ya no sirve: para un
    // Admin VL (LEVEL_1) el backend IMPONE `index=retailer` con el retailer de su token —frontera
    // territorial de F3, ver `PublicController.listBackofficeSales`— y descarta el `index`/`value`
    // del request. El filtro de tienda viajaba en el campo que el backend pisaba, así que elegir
    // "Centro CDMX" devolvía las ventas de toda la cadena, sin error visible.
    p.set('index', 'retailer');
    p.set('value', f.retailerId);
    if (f.storeId) p.set('storeId', f.storeId);
    if (f.sellerId) p.set('sellerId', f.sellerId);
    if (f.customerId) p.set('customerId', f.customerId);

    // `f.query` NO se manda. El endpoint acepta `q`, pero lo resuelve como
    // `contains(searchIndex, …)`: un FilterExpression que DDB evalúa DESPUÉS de leer la página, así
    // que un folio exacto que cae en la página 6 devuelve `count: 0` en la 1. Y `searchIndex` es
    // opcional en la fila: las ventas anteriores al snapshot no matchean nunca. La búsqueda se
    // resuelve en cliente sobre lo cargado (`filterByQuery`), que es además lo que hace el mock.
    if (f.provider) p.set('provider', f.provider);
    if (f.type) p.set('type', f.type);
    if (f.status) p.set('status', f.status);
    if (f.sku) p.set('sku', f.sku);
    if (f.centralSyncStatus) p.set('centralSyncStatus', f.centralSyncStatus);
    if (f.centralCancelStatus) p.set('centralCancelStatus', f.centralCancelStatus);
    // El rango se captura por DÍA pero se compara contra `soldAt`, que es un timestamp completo. Sin
    // expandirlo a instantes, `soldAt <= '2026-07-28'` excluye toda venta de ese mismo día (la
    // comparación de DDB es lexicográfica y `'2026-07-28T22:15…'` es mayor que `'2026-07-28'`).
    if (f.from) p.set('from', mxDayStartUtc(f.from));
    if (f.to) p.set('to', mxDayEndUtc(f.to));
    if (f.minPesos !== undefined) p.set('minCents', String(Math.round(f.minPesos * 100)));
    if (f.maxPesos !== undefined) p.set('maxCents', String(Math.round(f.maxPesos * 100)));

    // MÁS RECIENTE PRIMERO. El silo ordena ascendente por default (lo consume así el Home del
    // vendedor), pero en un explorador de ventas eso deja arriba las más viejas del histórico.
    //
    // Y no es solo preferencia: DynamoDB aplica los filtros DESPUÉS de leer la página, así que con
    // orden ascendente filtrar por una tienda cuyas ventas son recientes devolvía una primera página
    // VACÍA —con `nextToken`— y la pantalla decía "ninguna venta coincide" siendo falso.
    p.set('sortOrder', 'desc');

    return p.toString();
}

/** Una página de ventas tal como viaja, antes de normalizar el medio de cobro de cada fila. */
interface BackofficeSalesPageWire {
    items: BackofficeSaleWire[];
    count: number;
    nextToken: string | null;
}

export const backofficeSalesApi = {
    list: (
        f: BackofficeSalesFilters,
        nextToken?: string,
        limit = 50,
    ): Promise<BackofficeSalesPage> => {
        const qs = buildBackofficeSalesQuery(f);
        const extra = new URLSearchParams({ limit: String(limit) });
        if (nextToken) extra.set('nextToken', nextToken);
        return call<BackofficeSalesPageWire>(`/backoffice/sales?${qs}&${extra.toString()}`).then(
            (page) => ({ ...page, items: page.items.map(toBackofficeSale) }),
        );
    },
    /** Ventas de un cliente por su índice; el silo lo respeta en scope TENANT (Admin SK). */
    listByCustomer: (
        retailCustomerId: string,
        nextToken?: string,
    ): Promise<BackofficeSalesPage> => {
        const qs = new URLSearchParams({ index: 'customer', value: retailCustomerId, limit: '50' });
        if (nextToken) qs.set('nextToken', nextToken);
        return call<BackofficeSalesPageWire>(`/backoffice/sales?${qs.toString()}`).then((page) => ({
            ...page,
            items: page.items.map(toBackofficeSale),
        }));
    },
    get: (saleId: string): Promise<BackofficeSale> =>
        call<BackofficeSaleWire>(`/backoffice/sales/${encodeURIComponent(saleId)}`).then(
            toBackofficeSale,
        ),
    /**
     * `equipmentVerified` es obligatorio en el dominio: sin `true` el lambda responde 412.
     *
     * 🔴 **Va a `/backoffice/sales/…`, no a `/sales/…`.** Los dos endpoints existen y piden el mismo
     * permiso, pero con SCOPE distinto: el de `/sales` exige scope TENANT (la mesa central del
     * SOFOM) y el del backoffice exige RETAILER, que es el que tiene el Admin VL. Apuntando al
     * primero, un Admin VL recibía 403 al cancelar — con el endpoint correcto (E11) desplegado.
     */
    cancel: (
        saleId: string,
        reason: string,
        equipmentVerified: boolean,
        returnConditions: ReturnPieceCondition[] = [],
        outOfWindowExceptionId?: string,
    ): Promise<BackofficeSale> => {
        const body: components['schemas']['BackofficeCancelSaleRequest'] = {
            reason,
            equipmentVerified,
        };
        // Solo viaja lo dañado; sin condiciones todo vuelve en buen estado.
        if (returnConditions.length > 0) body.returnConditions = returnConditions;
        if (outOfWindowExceptionId) body.outOfWindowExceptionId = outOfWindowExceptionId;
        return post<BackofficeSaleWire>(
            `/backoffice/sales/${encodeURIComponent(saleId)}/cancel`,
            body,
            crypto.randomUUID(),
        ).then(toBackofficeSale);
    },
    /**
     * Los contadores del retailer en la ventana — no los de las ventas ya cargadas.
     *
     * Sin `from`/`to` el silo usa su ventana default. Se mandan solo cuando el filtro los tiene,
     * para no acotar la cuenta a un rango que el operador no pidió.
     */
    stats: (from?: string, to?: string) => {
        const qs = new URLSearchParams();
        if (from) qs.set('from', mxDayStartUtc(from));
        if (to) qs.set('to', mxDayEndUtc(to));
        const suffix = qs.toString();
        return call<BackofficeSalesStats>(`/backoffice/sales/stats${suffix ? `?${suffix}` : ''}`);
    },
    /** Regresa a PENDING un aviso REJECTED o FAILED de La Central, con el mismo saleId. */
    resendCentral: (saleId: string, kind: CentralResendKind): Promise<CentralResendResult> =>
        post<CentralResendResult>(
            `/backoffice/sales/${encodeURIComponent(saleId)}/central-resend`,
            { kind },
        ),
    /** La bitácora auditada de la venta. Una venta de otro retailer responde 404, no 403. */
    log: (saleId: string) =>
        call<SaleLogEntry[]>(`/backoffice/sales/${encodeURIComponent(saleId)}/log`),
};

/** Cancelación del vendedor sobre una venta de su tienda (`POST /portal/sales/{id}/cancel`). */
export const portalSalesApi = {
    cancel: (
        saleId: string,
        reason: string,
        equipmentVerified: boolean,
        returnConditions: ReturnPieceCondition[] = [],
    ): Promise<SaleSummary> => {
        const body: components['schemas']['PortalCancelSaleRequest'] = {
            reason,
            equipmentVerified,
        };
        // Solo viaja lo dañado; sin condiciones todo vuelve en buen estado.
        if (returnConditions.length > 0) body.returnConditions = returnConditions;
        return post<SaleSummary>(
            `/portal/sales/${encodeURIComponent(saleId)}/cancel`,
            body,
            crypto.randomUUID(),
        );
    },
};
